import { Controller } from '@hotwired/stimulus';
import {
  docTypeFromMimeType,
  driveUrlFromPickerDoc,
  fetchPickerConfig,
  loadGooglePickerApi,
  openGoogleDrivePicker
} from '../lib/google_drive_picker';

const COOKIE_NAME = 'bs_only_sheets_oauth_popup';
const POPUP_NAME = 'onlySheetsGoogleOAuth';

/** Staged OnlySheets dashboard: Google OAuth popup, Drive Picker, native dialogs. */
export default class extends Controller {
  static values = {
    pickerConfigUrl: String,
    openAccessAssetId: String
  };

  connect() {
    this._onMessage = this._onMessage.bind(this);
    window.addEventListener('message', this._onMessage);
    this._pickerOpening = false;
    this._openFlashAccessDialog();
    if (this.element.querySelector('[data-only-sheets-flash]')) {
      this._scrollToFlash();
    }
  }

  disconnect() {
    window.removeEventListener('message', this._onMessage);
  }

  openGoogleOAuth(event) {
    event.preventDefault();
    const url = event.currentTarget.getAttribute('href');
    if (!url) return;

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=1; Path=/; Max-Age=600; SameSite=Lax${secure}`;

    const w = 520;
    const h = 640;
    const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
    const features = `popup=yes,width=${w},height=${h},left=${left},top=${top}`;

    const popup = window.open(url, POPUP_NAME, features);
    if (!popup || popup.closed) {
      document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0${secure}`;
      window.alert('Allow pop-ups for this site to connect Google without leaving your dashboard.');
      return;
    }
    popup.focus();
  }

  async openDrivePicker(event) {
    event.preventDefault();
    if (this._pickerOpening) return;

    const mode = event.params.mode || 'add';
    const dialogId = event.params.dialogId || 'only-sheets-add-asset';
    const configUrl = this.pickerConfigUrlValue;
    if (!configUrl) {
      window.alert('Google Picker is not configured on this page.');
      return;
    }

    this._pickerOpening = true;
    const button = event.currentTarget;
    button.disabled = true;

    try {
      const config = await fetchPickerConfig(configUrl);
      await loadGooglePickerApi();

      openGoogleDrivePicker({
        config,
        onPick: (doc) => {
          if (mode === 'test') {
            window.alert(`Google connection works. You can access Drive as this account.\n\nSelected: ${doc.name}`);
            return;
          }

          const dialog = document.getElementById(dialogId);
          const autoSubmit = event.params.autoSubmit === true || event.params.autoSubmit === 'true';

          if (!dialog) return;

          if (!autoSubmit && typeof dialog.showModal === 'function' && !dialog.open) {
            dialog.showModal();
          }

          this._fillAddAssetForm(dialog, doc);

          if (autoSubmit) {
            dialog.querySelector('form')?.requestSubmit();
          }
        }
      });
    } catch (err) {
      window.alert(err.message || 'Could not open Google file picker.');
    } finally {
      button.disabled = false;
      this._pickerOpening = false;
    }
  }

  openDialog(event) {
    const id = event.params.dialogId;
    const el = document.getElementById(id);
    if (el && typeof el.showModal === 'function') el.showModal();
  }

  openAccessDialog(event) {
    const id = event.params.dialogId;
    const focus = event.params.focus;
    const el = document.getElementById(id);
    if (!el || typeof el.showModal !== 'function') return;

    el.showModal();

    if (focus === 'add') {
      const addSection = el.querySelector('[id^="only-sheets-access-add-"]');
      const input = addSection?.querySelector('textarea, input[type="email"]');
      if (addSection) addSection.scrollIntoView({ block: 'nearest' });
      input?.focus();
    }
  }

  closeDialog(event) {
    const dialog = event.target.closest('dialog');
    if (dialog && typeof dialog.close === 'function') dialog.close();
  }

  async copyText(event) {
    const text = event.params.text;
    if (!text) return;

    const button = event.currentTarget;
    const originalText = button.textContent;

    try {
      await navigator.clipboard.writeText(text);
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    } catch {
      window.alert('Copy failed. Select the URL and copy manually.');
    }
  }

  _openFlashAccessDialog() {
    const assetId = this.openAccessAssetIdValue;
    if (!assetId) return;

    const dialog = document.getElementById(`only-sheets-access-${assetId}`);
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;

    dialog.showModal();
    const feedback = dialog.querySelector('[data-only-sheets-access-feedback]');
    if (feedback) {
      feedback.scrollIntoView({ block: 'nearest' });
      return;
    }

    const addSection = dialog.querySelector('[id^="only-sheets-access-add-"]');
    addSection?.scrollIntoView({ block: 'nearest' });
  }

  _scrollToFlash() {
    const banner = this.element.querySelector('[data-only-sheets-flash]');
    if (!banner) return;

    banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  _fillAddAssetForm(dialog, doc) {
    const form = dialog.querySelector('form');
    if (!form) return;

    const urlField = form.querySelector('[name="only_sheets_asset[drive_url]"]');
    const typeField = form.querySelector('[name="only_sheets_asset[doc_type]"]');
    const nameField = form.querySelector('[name="only_sheets_asset[display_name]"]');

    const driveUrl = driveUrlFromPickerDoc(doc);
    if (urlField && driveUrl) urlField.value = driveUrl;

    // The url field is hidden now that picking is the only way in, so the
    // dialog has to say out loud which file it is about to add.
    const pickedFile = dialog.querySelector('[data-only-sheets-picked-file]');
    if (pickedFile) {
      pickedFile.textContent = doc.name ? `Chosen: ${doc.name}` : 'File chosen.';
    }

    const docType = docTypeFromMimeType(doc.mimeType);
    if (typeField) {
      typeField.value = docType;
      typeField.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (nameField && doc.name) {
      nameField.value = doc.name;
    }
  }

  _onMessage(event) {
    if (event.origin !== window.location.origin) return;
    const data = event.data;
    if (!data || data.type !== 'only-sheets-google-oauth') return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0${secure}`;
    if (data.status === 'success') {
      window.location.reload();
    } else if (data.message) {
      window.alert(data.message);
    }
  }
}
